// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { canSeeBillTrustLink } from "@pacific/App/Store/Selectors/GeneralSelectors";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showAs = "showAs",
    link = "link",
    title = "title",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAs]: "link" | "button";
        [fields.link]: string;
        [fields.title]: string;
        [fields.openInNewWindow]: boolean;
    };
}

interface BillTrustStyles {
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

const billTrustStyles: BillTrustStyles = {
    link: {
        color: "text.main",
        typographyProps: {
            size: 12,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin-bottom: 12px;
            `,
        },
    },
};

const styles = billTrustStyles;
const BillTrust: FC<OwnProps> = ({ fields: { showAs, title, link, openInNewWindow } }) => {
    const { showBillTrust } = useAppSelector(state => ({
        showBillTrust: canSeeBillTrustLink(state),
    }));

    if (!showBillTrust) {
        return null;
    }

    const onClickHandler = () => {
        typeof window !== "undefined" && window.open(`${link}`, openInNewWindow ? "_blank" : "");
    };

    if (showAs === "link") {
        return (
            <Link
                href={link}
                {...styles.link}
                data-test-selector="bill-trust-link"
                aria-label={title}
                target={openInNewWindow ? "_blank" : ""}
            >
                {title}
            </Link>
        );
    }

    return (
        <Button onClick={onClickHandler} {...styles.button} data-test-selector="bill-trust-link" aria-label={title}>
            {title}
        </Button>
    );
};

const widgetModule: WidgetModule = {
    component: BillTrust,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "BillTrust",
        fieldDefinitions: [
            {
                name: fields.showAs,
                editorTemplate: "DropDownField",
                defaultValue: "link",
                fieldType: "General",
                options: [
                    {
                        displayName: "Link",
                        value: "link",
                    },
                    {
                        displayName: "Button",
                        value: "button",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "BillTrust",
                fieldType: "General",
            },
            {
                name: fields.openInNewWindow,
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
